'use client';

import { InputFieldLabel } from '@tuic/web-compositions/shared/InputField';
import { RadixSelect } from '@tuic/web-compositions/shared/RadixSelect';
import { useState } from 'react';
import { ManifestCheckboxField, ManifestInputField, ManifestPhoneField, ManifestSelectField } from '../fields';
import { ManifestSection } from '../ManifestSection';
import { mapManifestOptionsToHtmlOptions } from '../utils';
import type { Manifest, ManifestContactData, ManifestFormValues, ManifestOption } from '../types';
import type { SimpleFormApi } from '@lib/form/helper';
import type { AnyFieldApi } from '@tanstack/react-form';
import type { I18NInterface } from '@tuic/web-compositions/utils/i18n';
import type { FC } from 'react';
const EMPTY_COPY_KEY = 'default_copy';
const emptyContactData: ManifestContactData = {
  streetAddress: '',
  zipCode: '',
  city: '',
  country: '',
  email: '',
  phone: '',
  mobile: '',
  approveMobilePhoneDisclosure: false
};
export type ContactSectionProps = {
  form: SimpleFormApi<ManifestFormValues>;
  manifest?: Manifest;
  disabled?: boolean;
  i18n?: I18NInterface<'contactTitle' | 'contactCopyFrom' | 'contactStreet' | 'contactPostalCode' | 'contactCity' | 'contactCountry' | 'contactEmail' | 'contactEmailNote' | 'contactPhone' | 'contactPhoneNote' | 'contactMobile' | 'contactMobileNote' | 'contactMobileConsent' | 'contactSelectPlaceholder'>;
};
export const ContactSection: FC<ContactSectionProps> = ({
  form,
  i18n,
  manifest,
  disabled
}) => {
  const options = manifest?.manifestOptions?.contact;
  const copyLookup: Record<string, ManifestContactData> = {
    [EMPTY_COPY_KEY]: emptyContactData,
    ...(options?.copyData ?? {})
  };
  const copyOptions: ManifestOption[] = [{
    value: EMPTY_COPY_KEY,
    label: i18n?.contactSelectPlaceholder ?? ''
  }, ...(options?.copyOptions ?? []).filter(o => o.value !== '')];
  const [copyFromKey, setCopyFromKey] = useState<string>(EMPTY_COPY_KEY);
  const handleCopy = (key: string): void => {
    setCopyFromKey(key);
    const data = copyLookup[key];
    if (!data) return;
    form.setFieldValue('contact.streetAddress', data.streetAddress ?? '');
    form.setFieldValue('contact.zipCode', data.zipCode ?? '');
    form.setFieldValue('contact.city', data.city ?? '');
    form.setFieldValue('contact.country', data.country ?? '');
    form.setFieldValue('contact.email', data.email ?? '');
    form.setFieldValue('contact.phone', data.phone ?? '');
    form.setFieldValue('contact.mobile', data.mobile ?? '');
    form.setFieldValue('contact.approveMobilePhoneDisclosure', data.approveMobilePhoneDisclosure ?? false);
  };
  return <ManifestSection pictogram="people" headline={i18n?.contactTitle} data-sentry-element="ManifestSection" data-sentry-component="ContactSection" data-sentry-source-file="ContactSection.tsx">
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <div className="block w-full space-y-1">
          {i18n?.contactCopyFrom && <InputFieldLabel asChild>
              <div>{i18n.contactCopyFrom}</div>
            </InputFieldLabel>}
          <RadixSelect ariaLabel={i18n?.contactCopyFrom} placeholder={i18n?.contactSelectPlaceholder} disabled={disabled} value={copyFromKey} onValueChange={handleCopy} options={copyOptions} data-sentry-element="RadixSelect" data-sentry-source-file="ContactSection.tsx" />
        </div>
        <form.Field name="contact.streetAddress" data-sentry-element="form.Field" data-sentry-source-file="ContactSection.tsx">
          {(field: AnyFieldApi) => <ManifestInputField field={field} required label={i18n?.contactStreet} className="w-full" disabled={disabled} />}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="contact.zipCode" data-sentry-element="form.Field" data-sentry-source-file="ContactSection.tsx">
          {(field: AnyFieldApi) => <ManifestInputField field={field} required label={i18n?.contactPostalCode} className="w-full" disabled={disabled} />}
        </form.Field>
        <form.Field name="contact.city" data-sentry-element="form.Field" data-sentry-source-file="ContactSection.tsx">
          {(field: AnyFieldApi) => <ManifestInputField field={field} required label={i18n?.contactCity} className="w-full" disabled={disabled} />}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="contact.country" data-sentry-element="form.Field" data-sentry-source-file="ContactSection.tsx">
          {(field: AnyFieldApi) => <ManifestSelectField field={field} required label={i18n?.contactCountry} placeholder={i18n?.contactSelectPlaceholder} options={mapManifestOptionsToHtmlOptions(options?.countryOptions)} className="w-full" disabled={disabled} />}
        </form.Field>
        <form.Field name="contact.email" data-sentry-element="form.Field" data-sentry-source-file="ContactSection.tsx">
          {(field: AnyFieldApi) => <ManifestInputField field={field} required label={i18n?.contactEmail} type="email" note={i18n?.contactEmailNote} className="w-full" disabled={disabled} />}
        </form.Field>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:gap-8">
        <form.Field name="contact.phone" data-sentry-element="form.Field" data-sentry-source-file="ContactSection.tsx">
          {(field: AnyFieldApi) => <ManifestPhoneField field={field} required label={i18n?.contactPhone} note={i18n?.contactPhoneNote} className="w-full" disabled={disabled} />}
        </form.Field>
        <form.Field name="contact.mobile" data-sentry-element="form.Field" data-sentry-source-file="ContactSection.tsx">
          {(field: AnyFieldApi) => <ManifestPhoneField field={field} required label={i18n?.contactMobile} note={i18n?.contactMobileNote} className="w-full" disabled={disabled} />}
        </form.Field>
      </div>

      <form.Field name="contact.approveMobilePhoneDisclosure" data-sentry-element="form.Field" data-sentry-source-file="ContactSection.tsx">
        {(field: AnyFieldApi) => <ManifestCheckboxField field={field} label={i18n?.contactMobileConsent} disabled={disabled} />}
      </form.Field>
    </ManifestSection>;
};